"use client";

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom"; 
import { supabase } from "../supabase/client";
import { 
  Package, ShoppingCart, Banknote, 
  Image as AdIcon, ArrowUpRight, TrendingUp,
  Layers, Zap
} from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    revenue: 0,
    activeAds: 0,
  });

  const [ loading, setLoading] = useState(true);

  useEffect(() => {
    // FIX 2: Define function INSIDE useEffect to solve dependency warning
    async function loadStats() {
      try {
        setLoading(true);
        const [
          { count: productCount }, 
          { count: orderCount }, 
          paidRevenue,
          { count: adCount }
        ] = await Promise.all([
            supabase.from("products").select("*", { count: "exact", head: true }),
            supabase.from("orders").select("*", { count: "exact", head: true }),
            supabase
              .from("orders")
              .select("total_amount")
              .eq("payment_status", "paid")
              .then(({ data }) => ({
                sum: data?.reduce((acc, row) => acc + (row.total_amount || 0), 0) || 0,
              })),
            supabase.from("ads").select("*", { count: "exact", head: true }).eq("is_active", true),
          ]);

        setStats({
          products: productCount || 0,
          orders: orderCount || 0,
          revenue: paidRevenue.sum || 0,
          activeAds: adCount || 0,
        });
      } catch (error) {
        console.error("Error loading dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    }

    loadStats();
  }, []); // Empty array is now safe because loadStats is defined inside

  // Optional: Add a loading state UI
  if (loading) {
    return <div className="p-10 text-xs font-black uppercase animate-pulse">Loading Analytics...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-10 font-sans text-slate-900">
      <div className="max-w-7xl mx-auto">
        
        {/* --- DYNAMIC HEADER --- */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="h-1 w-12 bg-indigo-600 rounded-full"></span>
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-600">Executive Overview</span>
            </div>
            <h1 className="text-4xl font-black tracking-tight uppercase italic">Control Panel</h1>
            <p className="text-slate-500 text-sm font-medium mt-1">Real-time store performance and logistics data.</p>
          </div>
          
          <div className="hidden md:flex items-center gap-3 bg-white p-2 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex -space-x-2">
               {[1,2,3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-slate-200" />)}
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase pr-2">System Active</p>
          </div>
        </div>

        {/* --- STATS GRID --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* REVENUE CARD (The "Hero" Stat) */}
          <Link to="/revenue" className="lg:col-span-2 group relative overflow-hidden bg-slate-900 rounded-[2.5rem] p-8 shadow-2xl shadow-slate-200 transition-all hover:-translate-y-1">
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
               <TrendingUp size={120} />
            </div>
            <div className="relative z-10 h-full flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-md">
                  <Banknote className="text-emerald-400" size={24} />
                </div>
                <div className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                  <Zap size={10} fill="currentColor"/> Live Earnings
                </div>
              </div>
              <div className="mt-12">
                <p className="text-white/50 text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Total Settled Revenue</p>
                <div className="flex items-baseline gap-2">
                   <h3 className="text-5xl font-black text-white italic transition-all group-hover:tracking-wider">
                     ৳{stats.revenue.toLocaleString()}
                   </h3>
                   <ArrowUpRight className="text-emerald-400 opacity-0 group-hover:opacity-100 transition-all" size={24}/>
                </div>
              </div>
            </div>
          </Link>

          {/* ORDERS CARD */}
          <Link to="/orders" className="group bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm transition-all hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-50">
            <div className="flex justify-between items-start mb-10">
              <div className="p-4 bg-blue-50 text-blue-600 rounded-2xl group-hover:bg-indigo-600 group-hover:text-white transition-all">
                <ShoppingCart size={24} />
              </div>
              <span className="text-[10px] font-black text-slate-300 uppercase">Fulfillment</span>
            </div>
            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Active Orders</p>
            <h3 className="text-4xl font-black text-slate-900 italic">{stats.orders}</h3>
            <div className="mt-4 flex items-center gap-2 text-[10px] font-bold text-blue-600 bg-blue-50 w-fit px-2 py-1 rounded">
              +12% vs last month
            </div>
          </Link>

          {/* PRODUCTS CARD */}
          <Link to="/products" className="group bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm transition-all hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-50">
            <div className="flex justify-between items-start mb-10">
              <div className="p-4 bg-pink-50 text-pink-600 rounded-2xl group-hover:bg-pink-600 group-hover:text-white transition-all">
                <Package size={24} />
              </div>
              <span className="text-[10px] font-black text-slate-300 uppercase">Inventory</span>
            </div>
            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Unique Skus</p>
            <h3 className="text-4xl font-black text-slate-900 italic">{stats.products}</h3>
            <div className="mt-4 flex items-center gap-2 text-[10px] font-bold text-pink-600 bg-pink-50 w-fit px-2 py-1 rounded">
              Live in store
            </div>
          </Link>

        </div>

        {/* --- SECONDARY ROW --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          
          {/* ADS BANNER CARD */}
          <Link to="/ads" className="lg:col-span-1 group bg-indigo-50 rounded-[2.5rem] p-8 border border-indigo-100 flex flex-col justify-between transition-all hover:bg-indigo-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white rounded-xl shadow-sm text-indigo-600">
                <AdIcon size={20} />
              </div>
              <h4 className="font-black text-sm uppercase italic tracking-wider text-indigo-900">Promotion Control</h4>
            </div>
            <div className="mt-8">
              <h3 className="text-5xl font-black text-indigo-900 italic">{stats.activeAds}</h3>
              <p className="text-[10px] font-bold text-indigo-600/60 uppercase tracking-widest mt-1">Active Marketing Banners</p>
            </div>
          </Link>

          {/* QUICK LINKS / SYSTEM STATUS */}
          <div className="lg:col-span-2 bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm">
             <div className="flex items-center gap-2 mb-6">
               <Layers size={18} className="text-slate-400" />
               <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">System Shortcuts</h4>
             </div>
             <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Site Config', path: '/siteSettings' },
                  { label: 'Products Gallery', path: '/gallery' },
                  { label: 'Coupons', path: '/coupons' },
                  { label: 'Reports', path: '/revenue' },
                  { label: 'Size Guide', path: '/size-guide' },
                  { label: 'Privacy Policy', path: '/privacy-policy' }
                ].map((item) => (
                  <Link key={item.label} to={item.path} className="flex flex-col items-center justify-center p-4 bg-slate-50 rounded-2xl hover:bg-slate-900 hover:text-white transition-all group">
                    <div className="text-[10px] font-black uppercase tracking-tighter">{item.label}</div>
                  </Link>
                ))}
             </div>
          </div>

        </div>
      </div>
    </div>
  );
}